<template>
	<div class="agent-task-status-wrapper">
		<!-- 状态卡片容器 - 横向滚动 -->
		<div class="status-cards-container" ref="container">
			<!-- 遍历状态历史 -->
			<div
				v-for="(item, index) in statusHistory"
				:key="index"
				class="status-card"
				:class="{
					'collapsed': item.collapsed && index < statusHistory.length - 1,
					'latest': index === statusHistory.length - 1,
					[`status-${item.status}`]: true
				}"
				@click="handleCardClick(item, index)">

				<!-- 折叠状态：只显示图标 -->
				<div v-if="item.collapsed && index < statusHistory.length - 1" class="collapsed-content">
					<i :class="getCollapsedIcon(item, index)"></i>
				</div>

				<!-- 展开状态：显示详细内容 -->
				<div v-else class="expanded-content">
					<!-- 状态头部 -->
					<div class="status-header">
						<i :class="getStatusIcon(item.status)"></i>
						<span class="status-title">{{ getStatusTitle(item.status) }}</span>
						<span class="status-time">{{ formatTime(item.timestamp) }}</span>
					</div>

					<!-- 状态内容 -->
					<div class="status-body">
						<p class="status-message">{{ item.message }}</p>

						<!-- 确认问题面板 -->
						<ConfirmationPanel
							v-if="isLatestStatus(index) && taskStatusData.confirmationPanel"
							:panel-data="taskStatusData.confirmationPanel"
							@submit="handleConfirmationSubmit"
						/>

						<!-- 任务结果 -->
						<TaskResult
							v-if="isLatestStatus(index) && taskStatusData.result"
							:result-data="taskStatusData.result"
						/>
					</div>
				</div>
			</div>

			<!-- 折叠/展开按钮 -->
			<div v-if="hasHiddenStatus" class="toggle-button" @click="showAllStatus = !showAllStatus">
				<i :class="showAllStatus ? 'el-icon-d-arrow-left' : 'el-icon-more'"></i>
			</div>
		</div>

		<!-- 🔥 状态详情弹窗 -->
		<el-dialog
			:visible.sync="detailDialogVisible"
			:title="detailDialogData?.status ? getStatusTitle(detailDialogData.status) : '状态详情'"
			width="500px"
			:append-to-body="true"
			:close-on-click-modal="true"
			custom-class="agent-status-detail-dialog">
			<div v-if="detailDialogData" class="detail-dialog-content">
				<!-- 状态图标和时间 -->
				<div class="detail-header">
					<i :class="getStatusIcon(detailDialogData.status)" :style="{ color: getStatusColor(detailDialogData.status) }"></i>
					<span class="detail-time">{{ formatTime(detailDialogData.timestamp) }}</span>
				</div>

				<!-- 状态消息 -->
				<div class="detail-message">
					{{ detailDialogData.message }}
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import ConfirmationPanel from './ConfirmationPanel.vue';
import TaskResult from './TaskResult.vue';

export default {
	name: 'AgentTaskStatusCard',
	components: {
		ConfirmationPanel,
		TaskResult
	},
	props: {
		taskStatusData: {
			type: Object, // AgentTaskStatusContent
			required: true
		}
	},
	data() {
		return {
			detailDialogVisible: false,
			detailDialogData: null,
			showAllStatus: false // 是否显示所有状态
		};
	},
	computed: {
		statusHistory() {
			const history = this.taskStatusData.statusHistory || [];
			// 如果展开或状态少于等于2个，显示全部
			if (this.showAllStatus || history.length <= 2) {
				return history;
			}
			// 否则只显示第一个和最后一个
			return [history[0], history[history.length - 1]];
		},
		hasHiddenStatus() {
			const history = this.taskStatusData.statusHistory || [];
			return history.length > 2;
		}
	},
	mounted() {
		this.scrollToLatest();
	},
	updated() {
		this.scrollToLatest();
	},
	methods: {
		// 自动滚动到最新卡片
		scrollToLatest() {
			this.$nextTick(() => {
				const container = this.$refs.container;
				if (container) {
					container.scrollLeft = container.scrollWidth;
				}
			});
		},

		// 判断是否为最新状态
		isLatestStatus(index) {
			return index === this.statusHistory.length - 1;
		},

		// 获取状态图标（展开状态使用）
		getStatusIcon(status) {
			const iconMap = {
				'initializing': 'el-icon-loading',
				'running': 'el-icon-loading',
				'waiting_confirmation': 'el-icon-question',
				'completed': 'el-icon-circle-check',
				'error': 'el-icon-circle-close'
			};
			return iconMap[status] || 'el-icon-info';
		},

		// 获取折叠状态的图标
		getCollapsedIcon(item, index) {
			// 如果当前状态是 error，显示错误图标
			if (item.status === 'error') {
				return 'el-icon-circle-close';
			}

			// 如果后续有状态（说明这个状态已经完成），显示对号
			// 只有最后一个折叠卡片才可能是未完成的
			if (index < this.statusHistory.length - 1) {
				const nextStatus = this.statusHistory[index + 1]?.status;

				// 如果下一个状态存在且不是error，说明当前状态已完成
				if (nextStatus && nextStatus !== 'error') {
					return 'el-icon-circle-check';
				}
			}

			// 其他情况使用原始图标
			return this.getStatusIcon(item.status);
		},

		// 获取状态标题
		getStatusTitle(status) {
			const titleMap = {
				'initializing': '初始化',
				'running': '执行中',
				'waiting_confirmation': '等待确认',
				'completed': '已完成',
				'error': '执行失败'
			};
			return titleMap[status] || '未知状态';
		},

		// 格式化时间
		formatTime(timestamp) {
			const date = new Date(timestamp);
			const now = new Date();
			const diff = now - date;

			// 小于1分钟显示"刚刚"
			if (diff < 60000) {
				return '刚刚';
			}

			// 小于1小时显示"X分钟前"
			if (diff < 3600000) {
				return Math.floor(diff / 60000) + '分钟前';
			}

			// 小于24小时显示"X小时前"
			if (diff < 86400000) {
				return Math.floor(diff / 3600000) + '小时前';
			}

			// 否则显示具体时间
			const hours = date.getHours().toString().padStart(2, '0');
			const minutes = date.getMinutes().toString().padStart(2, '0');
			return `${hours}:${minutes}`;
		},

		// 处理卡片点击
		handleCardClick(item, index) {
			// 如果是折叠的历史卡片，显示详情弹窗
			if (index < this.statusHistory.length - 1 && item.collapsed) {
				this.detailDialogData = item;
				this.detailDialogVisible = true;
				return;
			}

			// 🔥 如果任务有 shareUrl，触发打开详情面板
			if (this.taskStatusData.shareUrl) {
				this.$emit('open-detail-panel', this.taskStatusData);
			} else {
				this.$message.warning('该任务暂无详情链接');
			}
		},

		// 获取状态颜色
		getStatusColor(status) {
			const colorMap = {
				'initializing': '#909399',
				'running': '#409eff',
				'waiting_confirmation': '#ff9500',
				'completed': '#67c23a',
				'error': '#f56c6c'
			};
			return colorMap[status] || '#909399';
		},

		// 处理确认问题提交
		handleConfirmationSubmit(answer) {
			this.$emit('confirmation-submit', answer);
		}
	}
}
</script>

<style scoped lang="scss">
.agent-task-status-wrapper {
	padding: 4px 0;

	.status-cards-container {
		display: flex;
		flex-direction: row;
		gap: 6px;
		overflow-x: auto;
		overflow-y: visible; // 允许垂直方向溢出
		padding: 2px 0 2px 4px; // 左侧增加padding为放大留空间
		scroll-behavior: smooth;

		// 隐藏滚动条但保持可滚动
		&::-webkit-scrollbar {
			height: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background: rgba(0, 0, 0, 0.2);
			border-radius: 2px;
		}

		.status-card {
			flex-shrink: 0;
			border-radius: 6px;
			border: 1px solid #e0e0e0;
			background: #fff;
			cursor: pointer;

			// 折叠状态：只显示图标,较窄
			&.collapsed {
				width: 32px;
				height: 32px;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				position: relative; // 使用相对定位
				overflow: visible;
				transition: transform 0.2s ease, box-shadow 0.2s ease;

				.collapsed-content {
					font-size: 16px;
					// 默认显示成功的绿色（历史状态默认都是已完成）
					color: #67c23a;

					// 如果显示的是错误图标，使用红色
					.el-icon-circle-close {
						color: #f56c6c;
					}
				}

				&:hover {
					transform: scale(1.15);
					box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
					z-index: 10; // 确保放大时在最上层
				}

				// error 状态的折叠卡片特殊处理
				&.status-error .collapsed-content {
					color: #f56c6c;
				}
			}

			// 展开状态：显示详细内容,较宽
			&:not(.collapsed) {
				min-width: 200px;
				max-width: 280px;
				padding: 8px 10px;
			}

			// 最新状态卡片：突出显示
			&.latest {
				border-color: #409eff;
				box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
			}

			// 不同状态的颜色
			&.status-initializing {
				border-color: #909399;

				.status-header i {
					color: #909399;
				}
			}

			&.status-running {
				border-color: #409eff;

				.status-header i {
					color: #409eff;
					animation: spin 1s linear infinite;
				}
			}

			&.status-waiting_confirmation {
				border-color: #ff9500;

				.status-header i {
					color: #ff9500;
				}
			}

			&.status-completed {
				border-color: #67c23a;

				.status-header i {
					color: #67c23a;
				}
			}

			&.status-error {
				border-color: #f56c6c;

				.status-header i {
					color: #f56c6c;
				}
			}
		}

		// 折叠/展开按钮
		.toggle-button {
			flex-shrink: 0;
			width: 32px;
			height: 32px;
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 6px;
			border: 1px solid #dcdfe6;
			background: #f5f7fa;
			cursor: pointer;
			transition: background 0.2s ease, border-color 0.2s ease; // 只保留 hover 动画

			i {
				font-size: 16px;
				color: #606266;
			}

			&:hover {
				background: #e4e7ed;
				border-color: #c0c4cc;
			}
		}
	}
}

// 状态详情样式
.expanded-content {
	.status-header {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 6px;

		i {
			font-size: 16px;
			flex-shrink: 0;
		}

		.status-title {
			flex: 1;
			font-weight: 600;
			font-size: 13px;
			color: #303133;
		}

		.status-time {
			font-size: 11px;
			color: #909399;
			flex-shrink: 0;
		}
	}

	.status-body {
		.status-message {
			font-size: 12px;
			color: #606266;
			line-height: 1.4;
			margin: 0 0 6px 0;
		}
	}
}

// 旋转动画
@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

// 🔥 状态详情弹窗样式
::v-deep .agent-status-detail-dialog {
	.el-dialog__header {
		padding: 16px 20px;
		border-bottom: 1px solid #ebeef5;
	}

	.el-dialog__body {
		padding: 20px;
	}

	.detail-dialog-content {
		.detail-header {
			display: flex;
			align-items: center;
			gap: 12px;
			margin-bottom: 16px;

			i {
				font-size: 24px;
			}

			.detail-time {
				font-size: 13px;
				color: #909399;
			}
		}

		.detail-message {
			font-size: 14px;
			color: #606266;
			line-height: 1.6;
			padding: 12px;
			background: #f5f7fa;
			border-radius: 6px;
		}
	}
}
</style>
